/**
 * Report Authoring View (Custom Report Engine)
 *
 * Tabbed authoring page for the three archetypes (pivot / listing / hierarchy).
 * Reached from ReportsView with ?scope=<scope> (a tenant UUID, or 'global') and
 * optionally an :id path param (edit an existing spec). On save, POSTs/PUTs the
 * spec with the target tenant in the body (tenant_id; null = global); the backend
 * validateSpecShape is the authoritative validator and its 400 message is
 * surfaced inline. Preview runs the spec and renders the envelope.
 *
 * Each archetype form is a child that receives (spec, onChange, targetTenantId)
 * and owns its own catalog fetches.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Icons } from '../common/Icons';
import { reportEngineService, marketService, reportCatalogService } from '../../services/api';
import PivotForm from './report-forms/PivotForm';
import ListingForm from './report-forms/ListingForm';
import HierarchyForm from './report-forms/HierarchyForm';
import ReportTypePicker from './report-forms/ReportTypePicker';
import ReportResultPane from './report-forms/ReportResultPane';

const ARCHETYPES = [
  { key: 'pivot', label: 'Pivot' },
  { key: 'listing', label: 'Listing' },
  { key: 'hierarchy', label: 'Hierarchy' },
];

// Empty starting spec per archetype.
const emptySpec = {
  pivot: { grain: '', dimension: { source: '', values: [] }, measure: { aggregate: 'SUM' }, companion_columns: [], include_other: false, summary: [] },
  listing: { record: '', columns: [], filters: [], sort: [] },
  hierarchy: { base_record: '', recurse_via: '', projection: [], anchor_filter: [], depth_cap: 20 },
};

const ReportAuthoringView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?scope= carries either a tenant UUID (specific) or the literal 'global'.
  // NOTE: param name is `scope`, NOT `tenant` — the Onboarding axios interceptor
  // reads ?tenant= on localhost and would inject it as a bogus X-Tenant-ID.
  const tenantParam = searchParams.get('scope') || '';
  const isGlobal = tenantParam === 'global';
  // Value passed to the service: a UUID for tenant-scoped, or null for global.
  const targetTenantId = isGlobal ? null : tenantParam;
  const isEdit = !!id;

  const [name, setName] = useState('');
  const [archetype, setArchetype] = useState('pivot');
  const [reportType, setReportType] = useState('');
  const [status, setStatus] = useState('draft');
  const [defaultRuntime, setDefaultRuntime] = useState({});
  const [reportTypeMeta, setReportTypeMeta] = useState(null);
  const [specByType, setSpecByType] = useState(emptySpec);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  // Preview state
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [marketEventId, setMarketEventId] = useState('');
  const [marketEvents, setMarketEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [marketVendors, setMarketVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [pivotGrains, setPivotGrains] = useState([]);

  const selectedPivotGrain = pivotGrains.find((g) => g.grain === specByType.pivot?.grain);
  const pivotNeedsMarketEvent = selectedPivotGrain?.runtime_param === 'marketEventId';
  const runtimeParamsFromCatalog = reportTypeMeta?.runtime?.params || [];
  const showTypePicker = !isEdit && !reportType && !isGlobal;

  const applyReportType = useCallback(async (typeSummary) => {
    setReportType(typeSummary.id);
    setArchetype(typeSummary.archetype);
    setError(null);
    try {
      const res = await reportCatalogService.getReportType(targetTenantId, typeSummary.id);
      const meta = res.data;
      setReportTypeMeta(meta);
      setSpecByType((prev) => ({ ...prev, [meta.archetype]: meta.default_spec }));
      setDefaultRuntime(meta.default_runtime || {});
      if (!name.trim()) setName(meta.label);
    } catch (err) {
      console.error('Failed to load report type', err);
      setError('Failed to load report type defaults.');
    }
  }, [targetTenantId, name]);

  useEffect(() => {
    const typeParam = searchParams.get('type');
    if (!isEdit && typeParam && !reportType) {
      applyReportType({ id: typeParam, archetype: 'pivot' });
    }
  }, [isEdit, searchParams, reportType, applyReportType]);

  useEffect(() => {
    if (archetype !== 'pivot' || isGlobal || !targetTenantId) { setPivotGrains([]); return; }
    (async () => {
      try {
        const res = await reportCatalogService.getGrains(targetTenantId);
        setPivotGrains(res.data || []);
      } catch (err) {
        console.error('Failed to load pivot grains', err);
        setPivotGrains([]);
      }
    })();
  }, [archetype, isGlobal, targetTenantId]);

  // For dimension pivots scoped to a market event, load the target tenant's events.
  useEffect(() => {
    if (archetype !== 'pivot' || isGlobal || !targetTenantId || !pivotNeedsMarketEvent) {
      setMarketEvents([]);
      return;
    }
    setLoadingEvents(true);
    (async () => {
      try {
        const res = await marketService.listEvents(targetTenantId);
        setMarketEvents(res.data || []);
      } catch (err) {
        console.error('Failed to load market events', err);
        setMarketEvents([]);
        if (err.response?.status === 403 && err.response?.data?.message) {
          setError(err.response.data.message);
        }
      } finally {
        setLoadingEvents(false);
      }
    })();
  }, [archetype, isGlobal, targetTenantId, pivotNeedsMarketEvent]);

  const needsVendorPicker = (runtimeParamsFromCatalog || []).some(
    (p) => p.key === 'vendorId' || p.type === 'marketVendor'
  );

  useEffect(() => {
    if (isGlobal || !targetTenantId || !needsVendorPicker) {
      setMarketVendors([]);
      return;
    }
    setLoadingVendors(true);
    (async () => {
      try {
        const res = await reportCatalogService.getMarketVendors(targetTenantId);
        setMarketVendors(res.data || []);
      } catch (err) {
        console.error('Failed to load market vendors', err);
        setMarketVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    })();
  }, [isGlobal, targetTenantId, needsVendorPicker]);

  // Guard: must have a scope (a tenant or global) selected upstream.
  useEffect(() => {
    if (!tenantParam) {
      setError('No report scope selected. Return to Custom Reports and pick a tenant or Global.');
    }
  }, [tenantParam]);

  // Load existing spec when editing.
  useEffect(() => {
    if (!isEdit || !tenantParam) { setLoading(false); return; }
    (async () => {
      try {
        const res = await reportEngineService.getSpec(targetTenantId, id);
        const s = res.data;
        setName(s.name);
        setArchetype(s.archetype);
        setReportType(s.report_type || '');
        setStatus(s.status || 'published');
        setDefaultRuntime(s.default_runtime || {});
        setSpecByType((prev) => ({ ...prev, [s.archetype]: s.spec }));
        if (s.report_type) {
          reportCatalogService.getReportType(targetTenantId, s.report_type)
            .then((res) => setReportTypeMeta(res.data))
            .catch(() => setReportTypeMeta(null));
        }
      } catch (err) {
        console.error('Failed to load spec', err);
        setError('Failed to load the report for editing.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id, targetTenantId]);

  const updateSpec = useCallback((next) => {
    setSpecByType((prev) => ({ ...prev, [archetype]: next }));
  }, [archetype]);

  const currentSpec = specByType[archetype];

  const handleSave = async () => {
    setError(null); setNotice(null);
    if (!name.trim()) { setError('Give the report a name.'); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        archetype,
        spec: currentSpec,
        report_type: reportType || undefined,
        status,
        default_runtime: defaultRuntime,
      };
      if (isEdit) {
        await reportEngineService.updateSpec(targetTenantId, id, payload);
        setNotice('Saved.');
      } else {
        const res = await reportEngineService.createSpec(targetTenantId, payload);
        setNotice('Created.');
        // Move to the edit URL so further saves update rather than duplicate.
        // Preserve the scope param ('global' or the tenant UUID).
        navigate('/admin/reports/' + res.data.id + '?scope=' + encodeURIComponent(tenantParam), { replace: true });
      }
    } catch (err) {
      console.error('Save failed', err);
      const msg = err.response?.data?.message || 'Save failed.';
      const details = err.response?.data?.errors;
      setError(details && details.length ? (msg + ': ' + details.map((d) => d.msg || d.message).join('; ')) : msg);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    if (!isEdit) { setError('Save the report once before previewing.'); return; }
    if (isGlobal) {
      setError('Global reports preview against a specific tenant. Run this report from Back Office as the tenant, or author a tenant-scoped copy to preview here.');
      return;
    }
    setError(null); setPreviewing(true); setPreview(null);
    try {
      const runtime = { ...defaultRuntime };
      if (runtime.vendorId) runtime.vendorId = parseInt(runtime.vendorId, 10);
      else delete runtime.vendorId;
      if (archetype === 'pivot' && pivotNeedsMarketEvent && marketEventId) {
        runtime.marketEventId = parseInt(marketEventId, 10);
      }
      const res = await reportEngineService.runSpec(targetTenantId, id, Object.keys(runtime).length ? runtime : undefined);
      setPreview(res.data);
    } catch (err) {
      console.error('Preview failed', err);
      setError(err.response?.data?.message || 'Preview run failed.');
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={48} className="spin" />
        <p>Loading…</p>
        <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/admin/reports')}>
          <Icons.ChevronLeft size={14} /> Back to reports
        </button>
      </div>

      {error && (
        <div className="alert alert-warning mb-3">
          <Icons.AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div className="alert alert-success mb-3">
          <Icons.CheckCircle size={18} />
          <span>{notice}</span>
        </div>
      )}

      {/* Scope indicator */}
      <div className="card mb-3" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Icons.Info size={16} />
        <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
          {isGlobal
            ? 'Authoring a GLOBAL report — available to all tenants, run against each tenant\'s own data.'
            : 'Authoring a report for a specific tenant.'}
        </span>
      </div>

      {/* Report type (V2) or legacy archetype tabs */}
      {showTypePicker ? (
        <div className="card mb-3" style={{ padding: '16px' }}>
          <ReportTypePicker targetTenantId={targetTenantId} onSelect={applyReportType} selectedId={reportType} />
        </div>
      ) : null}

      {!showTypePicker && (
      <>
      <div className="card mb-3" style={{ padding: '16px' }}>
        <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
          Report name
        </label>
        <input
          className="form-control"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Spend by Vendor"
          style={{ marginBottom: '16px' }}
        />

        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '16px' }}>
          <div style={{ minWidth: '200px' }}>
            <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
              Report type
            </label>
            <div style={{ fontSize: '14px', fontWeight: 500 }}>
              {reportTypeMeta?.label || reportType || ARCHETYPES.find((a) => a.key === archetype)?.label}
            </div>
          </div>
          <div style={{ minWidth: '160px' }}>
            <label className="form-label" style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'var(--text-muted)' }}>
              Status
            </label>
            <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="draft">Draft (super_admin only)</option>
              <option value="published">Published (tenant can run)</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        {!reportType && (
          <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
            {ARCHETYPES.map((a) => (
              <button
                key={a.key}
                onClick={() => { if (!isEdit) { setArchetype(a.key); setPreview(null); } }}
                disabled={isEdit && archetype !== a.key}
                className="btn btn-sm"
                style={{
                  borderRadius: '6px 6px 0 0',
                  border: 'none',
                  background: archetype === a.key ? 'var(--accent-primary, #2563eb)' : 'transparent',
                  color: archetype === a.key ? '#fff' : 'var(--text-muted)',
                  cursor: (isEdit && archetype !== a.key) ? 'not-allowed' : 'pointer',
                  opacity: (isEdit && archetype !== a.key) ? 0.4 : 1,
                }}
                title={isEdit ? 'Archetype is fixed when editing an existing report' : ''}
              >
                {a.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {runtimeParamsFromCatalog.some((p) => p.type === 'date' || p.type === 'enum' || p.key === 'vendorId') && (
        <div className="card mb-3" style={{ padding: '16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '12px' }}>Runtime defaults (optional)</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {runtimeParamsFromCatalog.filter((p) => p.type === 'date').map((p) => (
              <div key={p.key}>
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.label}</label>
                <input
                  type="date"
                  className="form-control"
                  value={defaultRuntime[p.key] || ''}
                  onChange={(e) => setDefaultRuntime((prev) => ({ ...prev, [p.key]: e.target.value }))}
                />
              </div>
            ))}
            {runtimeParamsFromCatalog.filter((p) => p.type === 'enum' && Array.isArray(p.options)).map((p) => (
              <div key={p.key}>
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.label}</label>
                <select
                  className="form-control"
                  value={defaultRuntime[p.key] || (p.options[0] && p.options[0].value) || ''}
                  onChange={(e) => setDefaultRuntime((prev) => ({ ...prev, [p.key]: e.target.value }))}
                >
                  {p.options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            ))}
            {runtimeParamsFromCatalog.filter((p) => p.key === 'vendorId' || p.type === 'marketVendor').map((p) => (
              <div key={p.key}>
                <label className="form-label" style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{p.label}</label>
                <select
                  className="form-control"
                  value={defaultRuntime.vendorId || ''}
                  onChange={(e) => setDefaultRuntime((prev) => ({ ...prev, vendorId: e.target.value || undefined }))}
                  disabled={loadingVendors}
                >
                  <option value="">{loadingVendors ? 'Loading vendors…' : 'All vendors'}</option>
                  {marketVendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active form */}
      <div className="card mb-3" style={{ padding: '16px' }}>
        {archetype === 'pivot' && (
          <PivotForm spec={currentSpec} onChange={updateSpec} targetTenantId={targetTenantId} />
        )}
        {archetype === 'listing' && (
          <ListingForm spec={currentSpec} onChange={updateSpec} targetTenantId={targetTenantId} />
        )}
        {archetype === 'hierarchy' && (
          <HierarchyForm spec={currentSpec} onChange={updateSpec} targetTenantId={targetTenantId} />
        )}
      </div>

      {/* Actions */}
      <div className="card mb-3" style={{ padding: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !tenantParam}>
          <Icons.Check size={18} /> {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create report')}
        </button>

        {archetype === 'pivot' && (
          isGlobal ? (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Global pivots preview against a specific tenant (run from Back Office).
            </span>
          ) : pivotNeedsMarketEvent ? (
            <select
              className="form-control"
              value={marketEventId}
              onChange={(e) => setMarketEventId(e.target.value)}
              style={{ width: '320px' }}
              disabled={loadingEvents}
            >
              <option value="">
                {loadingEvents ? 'Loading events…' : (marketEvents.length ? 'Select a market event…' : 'No market events found')}
              </option>
              {marketEvents.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {(ev.title || 'Event') + (ev.event_date ? ' — ' + ev.event_date.slice(0, 10) : '') + ' (#' + ev.id + ')'}
                </option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              No runtime options needed for this pivot grain.
            </span>
          )
        )}
        <button className="btn btn-secondary" onClick={handlePreview} disabled={previewing || !isEdit}>
          <Icons.Eye size={18} /> {previewing ? 'Running…' : 'Preview'}
        </button>
        {!isEdit && (
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Save once to enable preview.</span>
        )}
      </div>

      {/* Preview result */}
      {preview && (
        <div className="card" style={{ padding: '16px' }}>
          <h3 style={{ marginTop: 0 }}>Preview</h3>
          <ReportResultPane
            result={preview}
            specChart={specByType[archetype] && specByType[archetype].chart}
            typeDefaultChart={reportTypeMeta && reportTypeMeta.default_spec && reportTypeMeta.default_spec.chart}
          />
        </div>
      )}

      </>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ReportAuthoringView;
